<template>
  <Fragment>
    <pro-layout
      :collapse-transition="true"
      :side-menu="sideMenu"
      :is-active-micro="isActiveMicro"
      :layout="isSysImplant ? 'side' : 'mix'"
      :menu-render="isSysImplant ? false : settings.menuRender"
      :breadcrumb="true"
      :menu-list="menuList"
      :emit-hover="true"
      v-bind="settings"
      @sideListChange="sideListChange"
      @onMenuHeaderClick="onMenuHeaderChange"
      @onSideChange="onSideChange"
      @clickLogo="clickLogo"
      @clickItem="clickItem"
      @hoverItem="hoverItem"
      @onRemoveHover="removeHover"
    >
      <template #logo>
        <div class="gtr-logo">
          <img :src="gtrLogo" alt="" />
        </div>
      </template>
      <template #beforeNavBar>
        <BeforeNavBar />
      </template>
      <!--  面包屑    -->
      <template #breadcrumb>
        <Breadcrumb :sub-breadcrumb="subBreadcrumb" :gtr-breadcrumb-show="gtrBreadcrumbShow" />
      </template>
      <!--  工具栏    -->
      <template #tools>
        <Tools v-lazyload :count="Number(count) || 0" @onMessage="onMessage" />
      </template>
      <!--  个人中心    -->
      <template #profile>
        <Profile v-lazyload />
      </template>
      <!--  核心区域    -->
      <template #coreArea>
        <div style="width: 100%; height: 100%">
          <div v-if="noRebuildAppRoute" style="padding-top: 100px">
            <pb-empty
              :description="
                $t(
                  'Gattaran_Homepage__TQJz' /* 当前页面暂未接入新版Gattaran，如需访问请点击以下按钮 */,
                )
              "
            >
              <pb-button type="primary" @click="goOld"
                >{{ $t('Gattaran_Homepage__aeIE' /* 点击跳转查看 */) }}
              </pb-button>
            </pb-empty>
          </div>
          <Ticket />
          <slot name="mainCore">
            <router-view />
          </slot>
        </div>
      </template>
    </pro-layout>
    <top-navigation
      v-if="showPanel"
      :panel-data="panelData"
      @onMouseEnter="onMouseEnter"
      @onMouseLeave="removeHover"
      @handleChange="handleChange"
    />
  </Fragment>
</template>
<script>
import Vue, {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  getCurrentInstance,
  defineAsyncComponent,
} from 'vue'
import useInitLayout from './utils/useInitLayout'
import { useAppStore, useMenuStore, useStore } from '@/store'
import { settings } from '@/pages/layout/store'
import MessageCenterList from '@/components/message-center-list'
import MessageCenterCard from '@/components/message-center-card'
import FeedbackList from '@/components/feedback-center-list'
import { request } from '@/plugins/plugin-request'
import { env, prefixUrl, useRouter, useRoute } from '@/utils'
import { getAuthParams } from '@/plugins/plugin-sso-login'
import { setRecentVisit } from '../overview/service'
import { SUMMARY_KEY } from '@/config/constants'
import EventBus, { GTR_EVENT_KEYS } from '@/plugins/evnt-bus'
import {
  CLICK_TYPE,
  gtrCollectTrackCommonData,
  gtrTrackCK,
  gtrTrackSW,
} from '@/utils/gattaran-track-event'
import lodash from 'lodash'
import { OPEN_CLOSE, useOpendsStore } from '@/utils/tools'
import { isSystemImplant } from '@/utils/system-implant'
import { matchNoRebuildAppRoute } from '@/config/apps'
import { useMathSubModule } from '@/pages/layout/hooks'
import { eventBus, gtrSendEvents, globalStateActions } from '@/micro-app'
import useDriver from '@/plugins/driver/driver'
import { getMessageInstance } from '@/components/message-center-list/src/utils/useMessage'
// 异步组件加载
const Ticket = defineAsyncComponent(() => import('./components/ticket'))
const Tools = defineAsyncComponent(() => import('./components/tools'))
const Profile = defineAsyncComponent(() => import('./components/profile'))
const Breadcrumb = defineAsyncComponent(() => import('./components/breadcrumb'))
const BeforeNavBar = defineAsyncComponent(() => import('./components/beforeNavBar'))
const TopNavigation = defineAsyncComponent(() => import('./components/top-navigation/index.vue'))

/**
 * 埋点相关
 * @returns {{onSideChange({key?: *}): void, onMenuHeaderChange({key?: *}): void}}
 */
function useBuryDots() {
  const menuStore = useMenuStore()
  return {
    onSideChange(val, TOP_NAV = false) {
      // 推送最近访问模块,取nodeKey
      const {
        meta: { key = '', title = '', nodeId = '', appName = '' },
      } = menuStore.getRouteInfo(val?.key)
      setRecentVisit({
        // 是否是概述页，是的话指向父级
        nodeKey: key.includes(SUMMARY_KEY) ? key.split(SUMMARY_KEY)[0] : key,
        activeMicroAppName: appName,
      })
      // 微应用加载上报
      gtrCollectTrackCommonData({
        module: {
          name: title,
          nodeId,
        },
        entry_type: TOP_NAV ? CLICK_TYPE.TOP_NAV : CLICK_TYPE.ASIDE,
      })
      // 侧边栏点击上报
      gtrTrackCK({
        name: title,
        nodeId,
        click_type: TOP_NAV ? CLICK_TYPE.TOP_NAV : CLICK_TYPE.ASIDE,
      })
      // 顶部导航点击概览页发送曝光埋点
      key.includes(SUMMARY_KEY) &&
        TOP_NAV &&
        gtrTrackSW({
          isSPA: true,
          isMicro: false,
        })
    },
    onMenuHeaderChange(val) {
      const {
        meta: { title = '', nodeId = '' },
      } = menuStore.getRouteInfo(val?.key)
      // 微应用加载上报
      gtrCollectTrackCommonData({
        module: {
          name: title,
          nodeId,
        },
        entry_type: CLICK_TYPE.TOP,
      })
      gtrTrackSW({
        isSPA: true,
        isMicro: false,
      })
      gtrTrackCK({
        name: title,
        nodeId,
        click_type: CLICK_TYPE.TOP,
      })
    },
  }
}

/**
 * 初始化消息中心
 */
function useMessageCenter() {
  const userInfo = env.get('userInfo')
  Vue.use(MessageCenterList, {
    country: userInfo.country,
    timezone: userInfo.timezone,
    locale: userInfo.locale,
    username: userInfo.username,
  })
  Vue.use(MessageCenterCard, {})
  const messageInstance = getMessageInstance()
  const count = ref(0)
  // const cardCount = ref(0);
  onMounted(() => {
    // 监听消息状态
    // onReadStatus
    messageInstance.onMsgSniff(
      (err, res) => {
        if (err) {
          console.error(err)
        }
        // 普通消息
        if (res?.total !== undefined) count.value = res?.total || 0
        // 卡片消息
        if (res?.cardTotal !== undefined) {
          if (res?.cardTotal > 0) {
            const newMessage = res?.lists?.[0] || null
            const { msgContent, ...ext } = newMessage
            try {
              if (!lodash.isEmpty(msgContent)) {
                MessageCenterCard.open(
                  {
                    ...msgContent,
                    ...ext,
                  },
                  // () => {
                  //   console.log('putMsgRead');
                  //   putMsgRead({ isAll: 0, isCard: 1, msgIds: [ext.messageId] });
                  // },
                )
              }
            } catch (e) {
              console.warn(e)
            }
          }
        }
      },
      {
        isCard: 1,
      },
    )
  })
  return {
    count,
    onMessage() {
      MessageCenterList.open()
    },
  }
}

/**
 * 初始化反馈中心
 * @returns {{}}
 */
function useFeedbackList() {
  const userInfo = env.get('userInfo')
  Vue.use(FeedbackList, {
    country: userInfo.country,
    timezone: userInfo.timezone,
    locale: userInfo.locale,
    username: userInfo.username,
    config: {
      // apiEnv: process.env.NODE_ENV,
      request,
      prefixUrl,
      getAuthParams,
    },
  })
  return {}
}

/**
 * 初始化菜单信息
 * @returns {{menuList: *}}
 */
function useMenuList() {
  const menuList = ref([])
  const menuStore = useMenuStore()
  menuList.value = menuStore.routeList
  return {
    menuList,
  }
}

/**
 * 页面刷新后上报moduleInfo
 */
async function useModuleInfo() {
  onBeforeMount(() => {
    const appStore = useAppStore()
    // 页面曝光
    gtrTrackSW({
      module: appStore.$state.moduleInfo,
      isMicro: false,
    })
  })
}

/**
 * 保存当前模块概述所需的menuList数据（全量的）
 */
function useGetSideMenuList() {
  const store = useStore()
  const setCurrentList = key => {
    const allSideMenuMap = store.menu.getAllSideMenuMap
    if (!lodash.isEmpty(allSideMenuMap)) {
      store.menu.initCurrentSideList(key)
    }
  }
  watch(
    [() => store.menu.routeListAll, () => store.app.$state.moduleInfo],
    () => {
      const moduleInfo = store.app.$state.moduleInfo
      const sideKey = moduleInfo?.keys?.[0]
      setCurrentList(sideKey)
    },
    {
      deep: true,
    },
  )
}

// 处理侧边展出收起状态的
function useOpenedsHandler() {
  const opendsStore = useOpendsStore()
  const defaultOpeneds = ref(null)
  const sideMenu = reactive({
    isCollapse: +opendsStore.get()?.isCollapse === OPEN_CLOSE.open,
    defaultOpeneds,
    // 监听侧边导航展开收起
    collapseChange(evt) {
      sideMenu.isCollapse = evt
      sendCollapseEvent()
      if (evt) {
        opendsStore.open('isCollapse')
      } else {
        opendsStore.close('isCollapse')
      }
    },
    open(evt) {
      opendsStore.open(evt)
    },
    close(evt) {
      opendsStore.close(evt)
    },
  })
  const saveOpends = () => {
    opendsStore.save()
  }
  const sendCollapseEvent = () => {
    gtrSendEvents.GTR_EVENT_SEND_MENU_IS_COLLAPSE(!!sideMenu.isCollapse)
  }
  const setIsCollapse = collapse => {
    sideMenu.isCollapse = !!collapse
    sendCollapseEvent()
  }
  const getAsyncList = l => l.filter(i => !!i?.meta?.isAsync)
  const gtrSideCollapse = collapse => {
    setIsCollapse(collapse)
  }
  onBeforeMount(() => {
    defaultOpeneds.value = opendsStore.getOpens()
    window.addEventListener('beforeunload', saveOpends)
    // 监控子应用挂载事件，并发送展开收起状态
    eventBus.$on('GTR_EVENT_SEND_MICRO_APP_AFTER_MOUNT', sendCollapseEvent)
    // 注册事件：监听侧边展开收起
    eventBus.$on('GTR_LISTEN_EVENT_SIDE_COLLAPSE', gtrSideCollapse)
  })
  onBeforeUnmount(() => {
    opendsStore.save()
    window.removeEventListener('beforeunload', saveOpends)
    eventBus.$off('GTR_EVENT_SEND_MICRO_APP_AFTER_MOUNT', sendCollapseEvent)
    eventBus.$off('GTR_LISTEN_EVENT_SIDE_COLLAPSE', gtrSideCollapse)
  })

  return {
    setIsCollapse,
    sideMenu,
    sideListChange(evt) {
      if (evt?.value) {
        const nowSide = getAsyncList(evt.value)
        console.log('nowSide', nowSide)
        // 侧边二级子项只有一个的
        const onlyOneChild = nowSide.filter(i => i.children.length === 1).map(i => i.name)
        // 侧边小于2个业务模块时（除去概览页）默认展开
        const needOpens = nowSide.length <= 2 ? nowSide.map(i => i.name) : onlyOneChild
        // 过滤掉主动关闭的
        defaultOpeneds.value = [
          ...new Set([
            ...needOpens.filter(i => opendsStore.get()[i] !== 0),
            ...defaultOpeneds.value,
          ]),
        ]
      }
    },
  }
}

// 指引功能
function useStartDiver() {
  const driver = useDriver()
  onMounted(() => {
    driver.start()
  })
}
// 获取是否是移动端
function getMobile() {
  const { width } = document.body.getBoundingClientRect()
  return width - 1 < 570 // 宅屏幕适配
}
// 自适应
function useResize(callback) {
  const resizeHandler = lodash.debounce(() => {
    const isMobile = getMobile()
    callback(isMobile)
  }, 200)
  resizeHandler() // 第一次加载
  onMounted(() => {
    window.addEventListener('resize', resizeHandler)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('resize', resizeHandler)
  })
}
// 监听子系统路由参数变化
function useGlobalState(_this, opendsHandler) {
  // 基座面包屑
  const subBreadcrumb = ref([])
  const gtrBreadcrumbShow = ref(true)
  globalStateActions.onGlobalStateChange(state => {
    if (!Array.isArray(state.subBreadcrumb)) {
      console.error(`type error -- subBreadcrumb -- isArray, got ${state.subBreadcrumb}`)
      return
    }
    subBreadcrumb.value = state.subBreadcrumb
    gtrBreadcrumbShow.value = !!state.gtrBreadcrumbShow
    // 菜单定位
    const subMatchedKeys = state.subMatchedKeys
    if (subMatchedKeys && subMatchedKeys.length === 2) {
      if (subMatchedKeys?.[0]) {
        settings.defaultHeaderMenuSelect = subMatchedKeys?.[0]
      }
      if (subMatchedKeys?.[1]) {
        settings.defaultSideMenuSelect = subMatchedKeys?.[1]
        // 菜单折叠状态 打开
        const isMobile = getMobile()
        if (!isMobile) {
          opendsHandler && opendsHandler?.setIsCollapse(false)
        }
      }
    }
  })
  // 面包屑
  EventBus.$on(GTR_EVENT_KEYS.useBreadcrumb, state => {
    if (!Array.isArray(state.subBreadcrumb)) {
      console.error(`type error -- subBreadcrumb -- isArray, got ${state.subBreadcrumb}`)
      return
    }
    subBreadcrumb.value = state.subBreadcrumb
    gtrBreadcrumbShow.value = !!state.gtrBreadcrumbShow
  })
  return {
    subBreadcrumb,
    gtrBreadcrumbShow,
  }
}
let timer = null
export default {
  name: 'Layout',
  components: {
    // ProLayout,
    Tools,
    Profile,
    Breadcrumb,
    BeforeNavBar,
    TopNavigation,
    Ticket,
  },
  directives: {
    lazyload: {
      inserted(el) {
        // 默认不渲染
        el.style.display = 'none'
        // 添加懒加载逻辑，组件延迟加载
        setTimeout(() => {
          el.style.display = 'block'
          // 添加懒加载逻辑，组件延迟加载
        }, 300)
      },
    },
  },
  setup() {
    const opendsHandler = useOpenedsHandler()
    const isActiveMicro = computed(() => {
      const route = useRoute()
      return route?.path?.includes('/gtr_')
    })
    useStartDiver()
    useMathSubModule(null, opendsHandler)
    useModuleInfo()
    useGetSideMenuList()
    const buryDots = useBuryDots()
    const router = useRouter()
    const panelData = ref({
      event: {},
      item: {},
    })
    const showPanel = ref(false)
    const isSysImplant = ref(isSystemImplant())

    useResize(isMobile => {
      // 宽变窄时收起侧边栏
      if (isMobile) {
        opendsHandler.setIsCollapse(true)
      }
    })
    watch(showPanel, (newVal, oldVal) => {
      showPanel.value = newVal
    })
    function onMenuHeaderChange(evt, TOP_NAV = false) {
      const opendsStore = useOpendsStore()
      opendsStore.save()
      !TOP_NAV && buryDots.onMenuHeaderChange(evt)
    }
    const globalState = useGlobalState(getCurrentInstance?.()?.proxy, opendsHandler)

    return {
      isSysImplant,
      isActiveMicro,
      settings,
      cachedViews: [],
      panelData,
      showPanel,
      ...globalState,
      ...useMenuList(),
      ...useInitLayout(),
      ...useMessageCenter(),
      ...useFeedbackList(),
      onSideChange(evt) {
        buryDots.onSideChange(evt)
      },
      clickItem([_, route]) {
        router.push({
          name: route.name,
        })
      },
      hoverItem(val) {
        const { evt, itm } = val
        panelData.value.event = evt
        panelData.value.item = itm
        // 显示级联面板
        showPanel.value = true
      },
      // 不hover时 隐藏级联导航
      removeHover() {
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          // 关闭级联面板
          showPanel.value = false
        }, 300)
      },
      // 进入级联导航时 取消定时器
      onMouseEnter() {
        clearTimeout(timer)
      },
      // 顶部导航菜单点击
      handleChange(val) {
        showPanel.value = false
        router.push({
          name: val?.at(-1)?.name,
        })
        // 点击顶部概览 更新菜单信息
        onMenuHeaderChange({ key: val?.at(-1)?.meta?.parentName }, true)
        buryDots.onSideChange({ key: val?.at(-1)?.name }, true)
      },
      onMenuHeaderChange,
      ...opendsHandler,
    }
  },
  data() {
    return {
      noRebuildAppRoute: false,
      gtrLogo: require('@/assets/gtr-logo.png'),
    }
  },
  watch: {
    '$route.meta.menuRender': {
      handler(menuRender) {
        if (menuRender !== undefined) {
          this.settings.menuRender = menuRender
        } else {
          this.settings.menuRender = true
        }
      },
      immediate: true,
    },
    '$route.path': {
      immediate: false,
      handler(path) {
        // 子应用没有接入新版加特兰
        const route = this.$route
        this.noRebuildAppRoute = !!(matchNoRebuildAppRoute(path) || route?.meta?.httpUrl)
        useMathSubModule(route)
      },
    },
  },
  methods: {
    goOld() {
      const route = this.$route
      window.open(route.fullPath)
    },
  },
}
</script>
<style lang="scss" scoped>
.gtr-logo {
  img {
    width: 108px;
    vertical-align: middle;
    margin-top: -6px;
  }

  &:after {
    content: '';
    display: inline-block;
    width: 0;
    height: 100%;
    vertical-align: middle;
  }
}
// 顶部导航对齐
//::v-deep(.gtr_hidden_scroll) {
//  .pb-menu--horizontal {
//    line-height: 54px;
//  }
//}
</style>
